import { useEffect, useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { ChartCard } from '@/components/calculator/ChartCard';
import { FieldExplainer } from '@/components/calculator/FieldExplainer';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  ChartContainer,
  ChartLegendPills,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig
} from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { getEquityFieldExplainers } from '@/content/field-explainers/equity';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import {
  buildPortfolio,
  INSTRUMENT_LABELS,
  type Holding,
  type InstrumentKind,
  type PortfolioResult
} from '@/engines/equity-portfolio-engine';
import {
  TAX_PRESET_LABELS,
  fmt,
  type FilingStatus,
  type RsuVestFrequency,
  type TaxPresetKey
} from '@/engines/equity-core';
import {
  copyToClipboard,
  exportPdfReport,
  exportRowsToCsv,
  formatCurrency
} from './shared';

type PageProps = { route: RouteDefinition };

/** UI-facing global settings. Growth is stored as a percentage for display. */
type UiSettings = {
  horizon: number;
  currentPrice: number;
  growthPct: number;
  taxPreset: TaxPresetKey;
  filingStatus: FilingStatus;
};

type ShareableState = {
  settings: UiSettings;
  holdings: Holding[];
};

const PALETTE = ['#60a5fa', '#34d399', '#f59e0b', '#f472b6', '#a78bfa', '#22d3ee'];

const DEFAULT_SETTINGS: UiSettings = {
  horizon: 6,
  currentPrice: 10,
  growthPct: 12,
  taxPreset: 'nyc-single-2026',
  filingStatus: 'single'
};

const VEST_FREQ_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' }
];

const INSTRUMENT_OPTIONS: Array<{ value: InstrumentKind; label: string }> = [
  { value: 'rsu', label: INSTRUMENT_LABELS.rsu },
  { value: 'option', label: INSTRUMENT_LABELS.option },
  { value: 'espp', label: INSTRUMENT_LABELS.espp },
  { value: 'rsa', label: INSTRUMENT_LABELS.rsa },
  { value: 'sar', label: INSTRUMENT_LABELS.sar }
];

const chartConfig = {
  total: { label: 'Total value', color: 'var(--chart-1)' },
  vested: { label: 'Vested value', color: 'var(--chart-2)' },
  net: { label: 'After-tax (vested)', color: 'var(--chart-4)' }
} satisfies ChartConfig;

let holdingSeq = 0;

function makeHolding(kind: InstrumentKind, currentPrice: number): Holding {
  const idx = holdingSeq++;
  const base = {
    id: `holding-${Date.now().toString(36)}-${idx}`,
    label: INSTRUMENT_LABELS[kind],
    color: PALETTE[idx % PALETTE.length]
  };
  switch (kind) {
    case 'rsu':
      return {
        ...base,
        kind,
        vestedShares: 0,
        vestedBasisPerShare: currentPrice,
        unvestedShares: 4000,
        vestYears: 4,
        cliffMonths: 12,
        vestFrequency: 'annual'
      };
    case 'option':
      return {
        ...base,
        kind,
        optionType: 'iso',
        strike: 1,
        vestedShares: 0,
        unvestedShares: 20000,
        vestYears: 4,
        cliffMonths: 12,
        vestFrequency: 'annual',
        holdForLtcg: true,
        qsbsEligible: false
      };
    case 'espp':
      return {
        ...base,
        kind,
        contributionPerPeriod: 5000,
        periodsPerYear: 2,
        discountPct: 0.15,
        lookback: true,
        startYear: 1,
        endYear: 6,
        qualifying: true
      };
    case 'rsa':
      return {
        ...base,
        kind,
        totalShares: 100000,
        purchasePricePerShare: 0.01,
        grantPricePerShare: 0.05,
        election83b: true,
        vestYears: 4,
        cliffMonths: 12,
        vestFrequency: 'annual'
      };
    case 'sar':
      return {
        ...base,
        kind,
        settlement: 'full-value',
        baselinePrice: currentPrice,
        targetShares: 2000,
        performanceMultiplier: 1,
        vestYears: 4,
        cliffMonths: 12,
        vestFrequency: 'annual'
      };
  }
}

function defaultState(): ShareableState {
  return {
    settings: { ...DEFAULT_SETTINGS },
    holdings: [makeHolding('rsu', DEFAULT_SETTINGS.currentPrice)]
  };
}

function encodeState(state: ShareableState): string {
  try {
    return typeof window === 'undefined' ? '' : window.btoa(unescape(encodeURIComponent(JSON.stringify(state))));
  } catch {
    return '';
  }
}

function decodeState(encoded: string): Partial<ShareableState> | null {
  try {
    if (typeof window === 'undefined') return null;
    return JSON.parse(decodeURIComponent(escape(window.atob(encoded)))) as Partial<ShareableState>;
  } catch {
    return null;
  }
}

function readInitialState(): ShareableState {
  if (typeof window === 'undefined') return defaultState();
  const raw = new URLSearchParams(window.location.search).get('d');
  if (raw) {
    const parsed = decodeState(raw);
    if (parsed?.settings && Array.isArray(parsed.holdings)) {
      return {
        settings: { ...DEFAULT_SETTINGS, ...parsed.settings },
        holdings: parsed.holdings
      };
    }
  }
  return defaultState();
}

function formatCompactCurrency(value: number): string {
  return fmt(value);
}

export function EquityCalculatorPage({ route }: PageProps) {
  const { toast } = useToast();
  const [initial] = useState<ShareableState>(() => readInitialState());
  const [settings, setSettings] = useState<UiSettings>(initial.settings);
  const [holdings, setHoldings] = useState<Holding[]>(initial.holdings);
  const [addKind, setAddKind] = useState<InstrumentKind>('rsu');

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const result: PortfolioResult = useMemo(
    () =>
      buildPortfolio(holdings, {
        horizon: settings.horizon,
        currentPrice: settings.currentPrice,
        annualGrowthPct: settings.growthPct / 100,
        taxPreset: settings.taxPreset,
        filingStatus: settings.filingStatus
      }),
    [holdings, settings]
  );

  const now = result.portfolio[0];
  const end = result.portfolio[result.portfolio.length - 1];

  const explainers = useMemo(() => {
    const entries = getEquityFieldExplainers({
      input: {
        currentPrice: settings.currentPrice,
        growthPct: settings.growthPct,
        horizon: settings.horizon,
        filingStatus: settings.filingStatus,
        taxPreset: settings.taxPreset
      },
      result: {
        rates: result.rates,
        holdingsCount: holdings.length,
        vestedToday: now.vestedGross,
        netToday: now.vestedNet,
        projectedTotal: end.totalGross,
        netAtHorizon: end.vestedNet,
        endYear: end.yr
      }
    });
    const byId = new Map<string, FieldExplainerContent>(entries.map((entry) => [entry.id, entry]));
    return { entries, byId };
  }, [settings, result.rates, holdings.length, now, end]);

  const chartData = useMemo(
    () =>
      result.portfolio.map((y) => ({
        year: y.yr,
        total: Math.round(y.totalGross),
        vested: Math.round(y.vestedGross),
        net: Math.round(y.vestedNet)
      })),
    [result.portfolio]
  );

  function updateSettings(patch: Partial<UiSettings>) {
    setSettings((prev) => ({ ...prev, ...patch }));
  }

  function updateHolding(idx: number, patch: Partial<Holding>) {
    setHoldings((prev) => prev.map((h, i) => (i === idx ? ({ ...h, ...patch } as Holding) : h)));
  }

  function addHolding() {
    setHoldings((prev) => [...prev, makeHolding(addKind, settings.currentPrice)]);
  }

  function removeHolding(idx: number) {
    setHoldings((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleCopyShare() {
    const path = route.canonicalPath.endsWith('/') ? route.canonicalPath : `${route.canonicalPath}/`;
    const url = `${window.location.origin}${path}?d=${encodeState({ settings, holdings })}`;
    void copyToClipboard(url).then((ok) =>
      toast({
        title: ok ? 'Share link copied' : 'Share link ready',
        description: ok ? 'Your equity portfolio link is on the clipboard.' : `Copy manually: ${url}`,
        variant: ok ? 'success' : 'warning'
      })
    );
  }

  function handleExportCsv() {
    const headers = ['Year', 'Price', 'Vested value', 'Unvested value', 'Total value', 'After-tax (vested)', 'Ordinary tax this year'];
    const rows = result.portfolio.map((y) => [
      y.yr,
      Number(y.price.toFixed(2)),
      Math.round(y.vestedGross),
      Math.round(y.unvestedGross),
      Math.round(y.totalGross),
      Math.round(y.vestedNet),
      Math.round(y.ordinaryTaxThisYr)
    ]);
    exportRowsToCsv(headers, rows, 'equity-portfolio-projection.csv');
    toast({ title: 'CSV exported', description: 'equity-portfolio-projection.csv downloaded.', variant: 'success' });
  }

  async function handleExportPdf() {
    const ok = await exportPdfReport({
      title: 'Equity Portfolio Projection',
      subtitle: `${settings.horizon}-year horizon · ${settings.growthPct}% annual growth`,
      summaryLeft: [
        `Holdings: ${holdings.length}`,
        `Current price: ${formatCurrency(settings.currentPrice)}`,
        `Annual growth: ${settings.growthPct}%`,
        `Tax profile: ${TAX_PRESET_LABELS[settings.taxPreset]}`
      ],
      summaryRight: [
        `Vested today: ${formatCurrency(now.vestedGross)}`,
        `Net if sold today: ${formatCurrency(now.vestedNet)}`,
        `Projected total (yr ${end.yr}): ${formatCurrency(end.totalGross)}`,
        `Net if liquidated (yr ${end.yr}): ${formatCurrency(end.vestedNet)}`
      ],
      headers: ['Year', 'Price', 'Vested', 'Unvested', 'Total', 'After-tax'],
      rows: result.portfolio.map((y) => [
        y.yr,
        formatCurrency(y.price),
        formatCurrency(y.vestedGross),
        formatCurrency(y.unvestedGross),
        formatCurrency(y.totalGross),
        formatCurrency(y.vestedNet)
      ]),
      filename: 'equity-portfolio-projection.pdf'
    });
    toast({
      title: ok ? 'PDF exported' : 'PDF export unavailable',
      description: ok ? 'equity-portfolio-projection.pdf downloaded.' : 'PDF dependencies are not available right now.',
      variant: ok ? 'success' : 'warning'
    });
  }

  return (
    <AppLayout showRouteHowItWorks={false}>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">Equity Calculator</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Model your whole equity stack in one place — RSUs, stock options, ESPP, restricted stock, and SAR/phantom/PSU
          grants. Plot vested and unvested value year by year and see what it's worth after tax.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <AssumptionPanel
          title="Portfolio assumptions"
          description="A single share price path drives every holding. Set today's price, expected growth, and your tax profile."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <NumberField
              id="equityPrice"
              label="Current price / share"
              value={settings.currentPrice}
              min={0}
              step={0.5}
              prefix="$"
              explainer={explainers.byId.get('currentPrice')}
              onChange={(v) => updateSettings({ currentPrice: v })}
            />
            <NumberField
              id="equityGrowth"
              label="Expected annual growth"
              value={settings.growthPct}
              min={-50}
              max={200}
              step={1}
              suffix="%"
              explainer={explainers.byId.get('growthPct')}
              onChange={(v) => updateSettings({ growthPct: v })}
            />
            <NumberField
              id="equityHorizon"
              label="Years to project"
              value={settings.horizon}
              min={1}
              max={15}
              step={1}
              parseMode="integer"
              explainer={explainers.byId.get('horizon')}
              onChange={(v) => updateSettings({ horizon: Math.round(v) })}
            />
            <label className="block text-sm" htmlFor="equity-filing-status">
              <span className="mb-1 flex items-center gap-1.5 font-semibold text-foreground">
                Filing status
                {explainers.byId.get('filingStatus') ? <FieldExplainer explainer={explainers.byId.get('filingStatus')!} /> : null}
              </span>
              <Select
                id="equity-filing-status"
                value={settings.filingStatus}
                onChange={(e) => updateSettings({ filingStatus: e.target.value as FilingStatus })}
                options={[
                  { value: 'single', label: 'Single' },
                  { value: 'married_joint', label: 'Married filing jointly' }
                ]}
              />
            </label>
          </div>
          <label className="block text-sm" htmlFor="equity-tax-profile">
            <span className="mb-1 flex items-center gap-1.5 font-semibold text-foreground">
              Tax profile
              {explainers.byId.get('taxPreset') ? <FieldExplainer explainer={explainers.byId.get('taxPreset')!} /> : null}
            </span>
            <Select
              id="equity-tax-profile"
              value={settings.taxPreset}
              onChange={(e) => updateSettings({ taxPreset: e.target.value as TaxPresetKey })}
              options={(Object.keys(TAX_PRESET_LABELS) as TaxPresetKey[])
                .filter((k) => k !== 'custom')
                .map((k) => ({ value: k, label: TAX_PRESET_LABELS[k] }))}
            />
          </label>
        </AssumptionPanel>

        <div className="space-y-4">
          <ResultSummaryCard label="Vested value today" value={fmt(now.vestedGross)} note="shares you already own, at today's price" trend="flat" explainer={explainers.byId.get('vestedToday')} />
          <ResultSummaryCard label="Net if sold today" value={fmt(now.vestedNet)} note="after estimated tax on vested holdings" trend="flat" explainer={explainers.byId.get('netToday')} />
          <ResultSummaryCard label={`Projected total (yr ${end.yr})`} value={fmt(end.totalGross)} note="vested + unvested at projected price" trend="up" explainer={explainers.byId.get('projectedTotal')} />
          <ResultSummaryCard label={`Net if liquidated (yr ${end.yr})`} value={fmt(end.vestedNet)} note="after-tax cash from vested holdings" trend="up" explainer={explainers.byId.get('netAtHorizon')} />
          <ShareExportRow onCopyShare={handleCopyShare} onExportCsv={handleExportCsv} onExportPdf={handleExportPdf} />
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl text-foreground">Your holdings</h2>
            <p className="text-sm text-muted-foreground">Add each grant you hold or have been offered. Fields adapt to the instrument type.</p>
          </div>
          <div className="flex items-end gap-2">
            <label className="block text-sm" htmlFor="equity-add-instrument">
              <span className="mb-1 block font-semibold text-foreground">Instrument</span>
              <Select
                id="equity-add-instrument"
                value={addKind}
                onChange={(e) => setAddKind(e.target.value as InstrumentKind)}
                options={INSTRUMENT_OPTIONS}
              />
            </label>
            <Button onClick={addHolding}>Add holding</Button>
          </div>
        </div>

        {holdings.length === 0 ? (
          <Card className="brand-frame">
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No holdings yet. Choose an instrument above and add your first grant.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {holdings.map((holding, idx) => (
              <HoldingCard
                key={holding.id}
                holding={holding}
                horizon={settings.horizon}
                onUpdate={(patch) => updateHolding(idx, patch)}
                onRemove={() => removeHolding(idx)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <ChartCard
          title="Equity value over time"
          description="Total value tracks vested plus unvested holdings; the after-tax line shows net cash from vested shares."
          legend={<ChartLegendPills config={chartConfig} keys={['total', 'vested', 'net']} />}
        >
          <ChartContainer config={chartConfig} family="comparison">
            <LineChart accessibilityLayer data={chartData} margin={{ top: 18, left: 12, right: 12 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={(v) => `Yr ${v}`} />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} width={70} tickFormatter={(v) => formatCompactCurrency(Number(v))} />
              <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" formatter={(value) => formatCurrency(Number(value ?? 0))} />} />
              <Line dataKey="total" type="natural" stroke="var(--color-total)" strokeWidth={3.2} dot={false} />
              <Line dataKey="vested" type="natural" stroke="var(--color-vested)" strokeWidth={1.9} dot={false} />
              <Line dataKey="net" type="natural" stroke="var(--color-net)" strokeWidth={1.9} strokeDasharray="5 4" dot={false} />
            </LineChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Year-by-year detail" description="Vested vs unvested value, after-tax cash, and ordinary tax recognized at each vest.">
          <Table containerClassName="max-h-[28rem]" stickyHeader compact>
            <TableHeader>
              <TableRow>
                <TableHead numeric>Year</TableHead>
                <TableHead numeric>Price</TableHead>
                <TableHead numeric>Vested</TableHead>
                <TableHead numeric>Unvested</TableHead>
                <TableHead numeric>Total</TableHead>
                <TableHead numeric>After-tax</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.portfolio.map((y) => (
                <TableRow key={y.yr}>
                  <TableCell numeric>{y.yr}</TableCell>
                  <TableCell numeric>{formatCurrency(y.price)}</TableCell>
                  <TableCell numeric>{formatCurrency(y.vestedGross)}</TableCell>
                  <TableCell numeric>{formatCurrency(y.unvestedGross)}</TableCell>
                  <TableCell numeric>{formatCurrency(y.totalGross)}</TableCell>
                  <TableCell numeric>{formatCurrency(y.vestedNet)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      {holdings.length > 0 ? (
        <ChartCard title="Per-grant breakdown" description={`Each holding valued at the year ${end.yr} projected price.`}>
          <Table compact>
            <TableHeader>
              <TableRow>
                <TableHead>Holding</TableHead>
                <TableHead>Type</TableHead>
                <TableHead numeric>Shares</TableHead>
                <TableHead numeric>{`Value (yr ${end.yr})`}</TableHead>
                <TableHead numeric>{`Net (yr ${end.yr})`}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.holdings.map((proj) => {
                const last = proj.years[proj.years.length - 1];
                return (
                  <TableRow key={proj.id}>
                    <TableCell>
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: proj.color }} />
                        {proj.label}
                      </span>
                    </TableCell>
                    <TableCell>{INSTRUMENT_LABELS[proj.kind]}</TableCell>
                    <TableCell numeric>{Math.round(proj.totalShares).toLocaleString()}</TableCell>
                    <TableCell numeric>{formatCurrency(last.vestedGross + last.unvestedGross)}</TableCell>
                    <TableCell numeric>{formatCurrency(last.vestedNet)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ChartCard>
      ) : null}

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description="Every input and result on this page, explained with your numbers filled in. Tap the info icon next to any field for the same explanation in place. Per-grant holding fields aren't covered here since they vary by instrument type; these cover the shared assumptions driving every holding."
          entries={explainers.entries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Holding card
// ═══════════════════════════════════════════════════════════════════

function VestingFields({
  holding,
  onUpdate
}: {
  holding: Extract<Holding, { vestYears: number }>;
  onUpdate: (patch: Partial<Holding>) => void;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <NumberField id={`${holding.id}-vy`} label="Vest years" value={holding.vestYears} min={1} max={10} parseMode="integer" onChange={(v) => onUpdate({ vestYears: v } as Partial<Holding>)} />
      <NumberField id={`${holding.id}-cliff`} label="Cliff (months)" value={holding.cliffMonths} min={0} max={36} parseMode="integer" onChange={(v) => onUpdate({ cliffMonths: v } as Partial<Holding>)} />
      <label className="block text-sm" htmlFor={`${holding.id}-vest-freq`}>
        <span className="mb-1 block font-semibold text-foreground">Vest frequency</span>
        <Select id={`${holding.id}-vest-freq`} value={holding.vestFrequency} onChange={(e) => onUpdate({ vestFrequency: e.target.value as RsuVestFrequency } as Partial<Holding>)} options={VEST_FREQ_OPTIONS} />
      </label>
    </div>
  );
}

function HoldingCard({
  holding,
  horizon,
  onUpdate,
  onRemove
}: {
  holding: Holding;
  horizon: number;
  onUpdate: (patch: Partial<Holding>) => void;
  onRemove: () => void;
}) {
  return (
    <Card className="brand-frame" style={{ borderColor: `${holding.color}40` }}>
      <CardHeader className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 space-y-1">
            <input
              type="text"
              value={holding.label}
              onChange={(e) => onUpdate({ label: e.target.value })}
              className="w-full bg-transparent font-display text-lg text-foreground focus-visible:outline-none"
              aria-label="Holding name"
            />
            <Badge variant="secondary">{INSTRUMENT_LABELS[holding.kind]}</Badge>
          </div>
          <Button variant="outline" size="sm" onClick={onRemove}>
            Remove
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {holding.kind === 'rsu' ? (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <NumberField id={`${holding.id}-unv`} label="Unvested shares" value={holding.unvestedShares} min={0} step={100} parseMode="integer" onChange={(v) => onUpdate({ unvestedShares: v })} />
              <NumberField id={`${holding.id}-ves`} label="Already-vested shares" value={holding.vestedShares} min={0} step={100} parseMode="integer" onChange={(v) => onUpdate({ vestedShares: v })} />
              <NumberField id={`${holding.id}-basis`} label="Vested cost basis / share" value={holding.vestedBasisPerShare} min={0} step={0.5} prefix="$" helperText="FMV taxed at prior vest" onChange={(v) => onUpdate({ vestedBasisPerShare: v })} />
            </div>
            <VestingFields holding={holding} onUpdate={onUpdate} />
          </>
        ) : null}

        {holding.kind === 'option' ? (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="block text-sm" htmlFor={`${holding.id}-option-type`}>
                <span className="mb-1 block font-semibold text-foreground">Option type</span>
                <Select id={`${holding.id}-option-type`} value={holding.optionType} onChange={(e) => onUpdate({ optionType: e.target.value as 'iso' | 'nso' })} options={[{ value: 'iso', label: 'ISO' }, { value: 'nso', label: 'NSO' }]} />
              </label>
              <NumberField id={`${holding.id}-strike`} label="Strike price" value={holding.strike} min={0} step={0.25} prefix="$" onChange={(v) => onUpdate({ strike: v })} />
              <NumberField id={`${holding.id}-unv`} label="Unvested options" value={holding.unvestedShares} min={0} step={1000} parseMode="integer" onChange={(v) => onUpdate({ unvestedShares: v })} />
              <NumberField id={`${holding.id}-ves`} label="Already-vested options" value={holding.vestedShares} min={0} step={1000} parseMode="integer" onChange={(v) => onUpdate({ vestedShares: v })} />
            </div>
            <VestingFields holding={holding} onUpdate={onUpdate} />
            <div className="grid gap-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <Switch id={`${holding.id}-hold`} checked={holding.holdForLtcg} onCheckedChange={(c) => onUpdate({ holdForLtcg: c })} ariaLabel="Hold for LTCG" />
                Exercise &amp; hold ≥1yr for LTCG (ISO)
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <Switch id={`${holding.id}-qsbs`} checked={holding.qsbsEligible} onCheckedChange={(c) => onUpdate({ qsbsEligible: c })} ariaLabel="QSBS eligible" />
                QSBS §1202 eligible
              </label>
            </div>
          </>
        ) : null}

        {holding.kind === 'espp' ? (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <NumberField id={`${holding.id}-contrib`} label="Contribution / period" value={holding.contributionPerPeriod} min={0} step={500} prefix="$" onChange={(v) => onUpdate({ contributionPerPeriod: v })} />
              <NumberField id={`${holding.id}-periods`} label="Periods / year" value={holding.periodsPerYear} min={1} max={12} parseMode="integer" onChange={(v) => onUpdate({ periodsPerYear: v })} />
              <NumberField id={`${holding.id}-disc`} label="Discount" value={Math.round(holding.discountPct * 1000) / 10} min={0} max={50} step={1} suffix="%" onChange={(v) => onUpdate({ discountPct: v / 100 })} />
              <div className="grid grid-cols-2 gap-3">
                <NumberField id={`${holding.id}-start`} label="Start year" value={holding.startYear} min={1} max={horizon} parseMode="integer" onChange={(v) => onUpdate({ startYear: v })} />
                <NumberField id={`${holding.id}-end`} label="End year" value={holding.endYear} min={1} max={horizon} parseMode="integer" onChange={(v) => onUpdate({ endYear: v })} />
              </div>
            </div>
            <div className="grid gap-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <Switch id={`${holding.id}-lookback`} checked={holding.lookback} onCheckedChange={(c) => onUpdate({ lookback: c })} ariaLabel="Lookback" />
                Lookback (price the lower of offer / purchase date)
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <Switch id={`${holding.id}-qual`} checked={holding.qualifying} onCheckedChange={(c) => onUpdate({ qualifying: c })} ariaLabel="Qualifying disposition" />
                Qualifying disposition (held long enough)
              </label>
            </div>
          </>
        ) : null}

        {holding.kind === 'rsa' ? (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <NumberField id={`${holding.id}-shares`} label="Total shares" value={holding.totalShares} min={0} step={1000} parseMode="integer" onChange={(v) => onUpdate({ totalShares: v })} />
              <NumberField id={`${holding.id}-pp`} label="Purchase price / share" value={holding.purchasePricePerShare} min={0} step={0.01} prefix="$" onChange={(v) => onUpdate({ purchasePricePerShare: v })} />
              <NumberField id={`${holding.id}-gp`} label="FMV at grant / share" value={holding.grantPricePerShare} min={0} step={0.01} prefix="$" onChange={(v) => onUpdate({ grantPricePerShare: v })} />
            </div>
            <VestingFields holding={holding} onUpdate={onUpdate} />
            <label className="inline-flex items-center gap-2 text-sm">
              <Switch id={`${holding.id}-83b`} checked={holding.election83b} onCheckedChange={(c) => onUpdate({ election83b: c })} ariaLabel="83(b) election" />
              83(b) election (tax at grant, gains become LTCG)
            </label>
          </>
        ) : null}

        {holding.kind === 'sar' ? (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="block text-sm" htmlFor={`${holding.id}-settlement`}>
                <span className="mb-1 block font-semibold text-foreground">Settlement</span>
                <Select
                  id={`${holding.id}-settlement`}
                  value={holding.settlement}
                  onChange={(e) => onUpdate({ settlement: e.target.value as 'appreciation' | 'full-value' })}
                  options={[
                    { value: 'full-value', label: 'Full value (PSU)' },
                    { value: 'appreciation', label: 'Appreciation (SAR / phantom)' }
                  ]}
                />
              </label>
              <NumberField id={`${holding.id}-target`} label="Target shares / units" value={holding.targetShares} min={0} step={100} parseMode="integer" onChange={(v) => onUpdate({ targetShares: v })} />
              <NumberField id={`${holding.id}-mult`} label="Performance multiplier" value={holding.performanceMultiplier} min={0} max={5} step={0.1} helperText="1.0 = target payout" onChange={(v) => onUpdate({ performanceMultiplier: v })} />
              {holding.settlement === 'appreciation' ? (
                <NumberField id={`${holding.id}-baseline`} label="Baseline price" value={holding.baselinePrice} min={0} step={0.5} prefix="$" helperText="Appreciation measured above this" onChange={(v) => onUpdate({ baselinePrice: v })} />
              ) : null}
            </div>
            <VestingFields holding={holding} onUpdate={onUpdate} />
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
